import { getCookieChoices, hasCategoryConsent, type CookieConsentChoices } from './cookieConsent';

const MEASUREMENT_ID = (process.env.REACT_APP_GA_MEASUREMENT_ID || '').trim();
const GA_LINKER_DOMAINS = ['forumkapitalu.pl', 'app.forumkapitalu.pl'];

let bootstrapDone = false;
let loadPromise: Promise<void> | null = null;
let scriptLoadScheduled = false;
let scriptInjected = false;

type BootstrapOptions = {
  loadScript?: 'immediate' | 'deferred';
};

type ScriptWithReadyState = HTMLScriptElement & { readyState?: string };

export function getGaMeasurementId(): string | null {
  return MEASUREMENT_ID || null;
}

export function hasAnalyticsConsent(): boolean {
  return hasCategoryConsent('analytics');
}

function markGtagScriptLoaded(): void {
  window.__fkGaLoaded = true;
}

/**
 * Stub zgodny ze snippetem Google: dataLayer.push(arguments), nie tablica z rest params.
 */
function ensureGtagStub(): void {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag === 'function') {
    return;
  }
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
}

function isGtagScriptElementLoaded(script: HTMLScriptElement): boolean {
  if (window.__fkGaLoaded) {
    return true;
  }
  const readyState = (script as ScriptWithReadyState).readyState;
  if (readyState === 'loaded' || readyState === 'complete') {
    markGtagScriptLoaded();
    return true;
  }
  return false;
}

function getGtagScript(): HTMLScriptElement | null {
  return document.getElementById('fk-gtag-script') as HTMLScriptElement | null;
}

function waitForGtagScript(script: HTMLScriptElement): Promise<void> {
  if (isGtagScriptElementLoaded(script)) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const onLoad = () => {
      markGtagScriptLoaded();
      resolve();
    };
    const onError = () => {
      reject(new Error('Nie udało się załadować Google Analytics.'));
    };

    script.addEventListener('load', onLoad, { once: true });
    script.addEventListener('error', onError, { once: true });

    // Skrypt z cache: load mógł nastąpić zanim podpięliśmy listener.
    if (isGtagScriptElementLoaded(script)) {
      script.removeEventListener('load', onLoad);
      script.removeEventListener('error', onError);
      resolve();
    }
  });
}

function injectGtagScript(id: string): void {
  const existing = getGtagScript();
  if (existing) {
    scriptInjected = true;
    isGtagScriptElementLoaded(existing);
    return;
  }

  if (scriptInjected) {
    return;
  }

  scriptInjected = true;
  scriptLoadScheduled = true;
  const script = document.createElement('script');
  script.id = 'fk-gtag-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  script.onload = () => {
    markGtagScriptLoaded();
  };
  script.onerror = () => {
    loadPromise = null;
    scriptInjected = false;
  };
  document.head.appendChild(script);

  // Chromium: przy cache zdarzenie load bywa synchroniczne przy appendChild.
  if (!window.__fkGaLoaded) {
    isGtagScriptElementLoaded(script);
  }
}

function scheduleGtagScriptLoad(id: string, immediate = false): void {
  const existing = getGtagScript();
  if (existing) {
    scriptInjected = true;
    isGtagScriptElementLoaded(existing);
    return;
  }

  if (immediate) {
    injectGtagScript(id);
    return;
  }

  if (scriptLoadScheduled) return;
  scriptLoadScheduled = true;

  const run = () => injectGtagScript(id);
  if (typeof window === 'undefined') {
    return;
  }

  const schedule = () => {
    const w = window as Window & {
      requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
    };
    if (typeof w.requestIdleCallback === 'function') {
      w.requestIdleCallback(run, { timeout: 3000 });
      return;
    }
    window.setTimeout(run, 1800);
  };

  if (document.readyState === 'complete') {
    schedule();
    return;
  }

  window.addEventListener('load', schedule, { once: true });
}

/** Synchronizuje Consent Mode z wyborem w banerze cookie. */
export function syncGoogleConsentWithChoices(choices: CookieConsentChoices): void {
  bootstrapGoogleAnalytics({ loadScript: 'immediate' });
  applyConsentUpdate(choices);
}

function applyConsentUpdate(choices: CookieConsentChoices): void {
  ensureGtagStub();
  window.gtag!('consent', 'update', {
    analytics_storage: choices.analytics ? 'granted' : 'denied',
    ad_storage: choices.marketing ? 'granted' : 'denied',
    ad_user_data: choices.marketing ? 'granted' : 'denied',
    ad_personalization: choices.marketing ? 'granted' : 'denied',
  });
}

/**
 * Tag Google (gtag.js) od startu strony — wykrywalny przez GA / Tag Assistant.
 * Domyślnie Consent Mode: brak zapisu analityki do czasu zgody użytkownika.
 */
export function bootstrapGoogleAnalytics(options?: BootstrapOptions): void {
  const id = getGaMeasurementId();
  if (!id) return;

  if (!bootstrapDone) {
    bootstrapDone = true;
    ensureGtagStub();
    window.gtag!('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      wait_for_update: 500,
    });
    window.gtag!('js', new Date());
    window.gtag!('config', id, {
      send_page_view: false,
      linker: {
        domains: GA_LINKER_DOMAINS,
        accept_incoming: true,
      },
    });

    try {
      if (hasCategoryConsent('analytics') || hasCategoryConsent('marketing')) {
        applyConsentUpdate(getCookieChoices());
      }
    } catch {
      /* private mode / brak localStorage */
    }
  }

  scheduleGtagScriptLoad(id, options?.loadScript === 'immediate');
}

/** Po zgodzie (lub przy wcześniejszej zgodzie) — włącza zapis w GA. */
export function loadGoogleAnalytics(): Promise<void> {
  const id = getGaMeasurementId();
  if (!id) {
    return Promise.resolve();
  }

  bootstrapGoogleAnalytics({ loadScript: 'immediate' });

  if (hasAnalyticsConsent()) {
    try {
      applyConsentUpdate(getCookieChoices());
    } catch {
      applyConsentUpdate({ necessary: true, functional: false, analytics: true, marketing: false });
    }
  }

  if (window.__fkGaLoaded) {
    return Promise.resolve();
  }

  const script = getGtagScript();
  if (!script) {
    return Promise.reject(new Error('Brak skryptu Google Analytics.'));
  }

  if (isGtagScriptElementLoaded(script)) {
    return Promise.resolve();
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = waitForGtagScript(script).finally(() => {
    loadPromise = null;
  });

  return loadPromise;
}

export function trackPageView(pagePath: string, pageTitle?: string): void {
  const id = getGaMeasurementId();
  if (!id || !hasAnalyticsConsent() || typeof window.gtag !== 'function') {
    return;
  }
  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle ?? document.title,
    send_to: id,
  });
}

type PurchaseAttribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
};

/** Konwersja zakupu — z atrybucją UTM z reflinka / filmiku. */
export function trackPurchase(transactionId: string, attribution?: PurchaseAttribution | null): void {
  const id = getGaMeasurementId();
  if (!id || !hasAnalyticsConsent() || typeof window.gtag !== 'function') {
    return;
  }

  const payload: Record<string, unknown> = {
    transaction_id: transactionId,
    currency: 'PLN',
    send_to: id,
    items: [
      {
        item_name: 'Forum Kapitału — subskrypcja',
        item_category: attribution?.utm_campaign || 'bez_kampanii',
      },
    ],
  };

  if (attribution?.utm_source) payload.utm_source = attribution.utm_source;
  if (attribution?.utm_medium) payload.utm_medium = attribution.utm_medium;
  if (attribution?.utm_campaign) payload.utm_campaign = attribution.utm_campaign;
  if (attribution?.utm_content) payload.utm_content = attribution.utm_content;
  if (attribution?.utm_term) payload.utm_term = attribution.utm_term;

  window.gtag('event', 'purchase', payload);
}

/** Po odświeżeniu — jeśli zgoda analityczna już zapisana. */
export function initGoogleAnalyticsIfConsented(): void {
  bootstrapGoogleAnalytics({ loadScript: 'immediate' });
  if (!hasAnalyticsConsent()) {
    return;
  }
  void loadGoogleAnalytics()
    .then(() => {
      trackPageView(`${window.location.pathname}${window.location.search}`);
    })
    .catch(() => {
      /* sieć / adblock */
    });
}
